/**
 * JkautocompleteComponent
 *
 * 基於 Angular Material 自動完成功能的組件，提供帶有過濾功能的下拉選擇器。
 * 允許使用者從預定義的項目列表中選擇一個項目，並在使用者輸入時提供即時過濾和建議。
 *
 * @see jkautocomplete.component.md 完整文檔
 */
import { CommonModule } from '@angular/common';
import { Component, Input, output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormControlDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { map, Observable, startWith } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
//import { AsyncPipe } from '@angular/common';
import { MaterialModule } from '../../material.module';
import { jkLib } from '../../services/jkCommon';
import { JkBaseComponent } from '../jkbase/jkbase.component';
import { idNameItem } from '../../services/jkInterface';
//import { JkfieldlabelComponent } from '../jkfieldlabel/jkfieldlabel.component';

@Component({
  selector: 'app-jkautocomplete',
  standalone: true,
  imports: [
    //JkfieldlabelComponent,
    MaterialModule,
    CommonModule,
    MatAutocompleteModule,
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
  ],
  templateUrl: './jkautocomplete.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './jkautocomplete.component.css',
})
/**
 * 自動完成組件
 * 提供帶有過濾功能的下拉選擇器，支援 ID/名稱格式的項目資料結構
 */
export class JkautocompleteComponent extends JkBaseComponent {
  /** 組件類型識別 */
  override comType: string = 'jkautocomplete';

  /**
   * 取得工具提示文字
   * @returns 包含標籤和使用提示的工具提示文字
   */
  get myTooltip():string{
    return this.label()+", 按[enter]確認輸入"
  };

  /**
   * 處理值變更事件
   * 當輸入值變更時觸發，並發出變更事件
   * @param $event 輸入事件
   */
  onValueChange($event: Event) {
    //throw new Error('Method not implemented.');
    let value = ($event.target as HTMLInputElement).value;
    //console.log($event);
    if(this.valueChange)
      this.valueChange.emit(value);
  }

  /** 項目列表內部存儲 */
  _itemList: idNameItem[] = [];

  /**
   * 取得項目列表
   * @returns 可供選擇的項目列表
   */
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() get itemList(): idNameItem[]{
    return this._itemList;
  };

  /**
   * 設定項目列表
   * @param value 可供選擇的項目列表
   */
  set itemList(value: idNameItem[]){
    this._itemList = value;
  }

  /** 當前選中的項目 */
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() item: idNameItem = { id: '', name: '' };

  /** 內部值存儲 */
  _value: string = '';

  /**
   * 取得當前值
   * @returns 當前選中項目的 ID
   */
  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() get value(): string {
    return this._value;
  }

  /**
   * 設定當前值
   * 會根據 ID 查找對應的項目，並更新當前選中項
   * @param v 要設定的項目 ID
   */
  set value(v: string) {
    if(v === this._value) return;
    this.item = this.itemList.find((item) => item.id === v) || {
      id: '',
      name: '',
    };
    if (this.item.id === v) {// 有找到
      this._value = v;
    }
  }

  /** 值變更事件發射器 */
  readonly valueChange = output<string>();
  /** 表單控制項，用於管理自動完成的輸入值 */
  myControl = new FormControl('');

  /** 過濾後的選項列表 Observable */
  filteredOptions!: Observable<idNameItem[]>;

  /**
   * 過濾選項方法
   * 根據輸入值過濾項目列表
   * @param value 輸入的過濾值
   * @returns 過濾後的項目列表
   */
  private _filter(value: string): idNameItem[] {
    const filterValue = value.toLowerCase();
    /*let r = this.stkmaLookup.find(option => {
      let bool = (option.idName as string).toLowerCase().includes(filterValue);
      if(bool) return option
      else return undefined;*/
    return this.itemList.filter((option) => {
      let bool = (option.idName as string).toLowerCase().includes(filterValue);
      return bool;
    });
    //return this.options.filter(option => {  option.toLowerCase().includes(filterValue) });
  }

  /**
   * 處理按下 Enter 鍵事件
   * 當使用者按下 Enter 鍵時確認選擇
   * @param e 輸入事件
   */
  onValueEnter(e: any) {
    let temp = e.target.value;
    this.item = this.itemList.find((item) => item.id === temp) || {
      id: '',
      name: '',
    };
    if(this.item.id === temp) {// 有找到
      this.value = temp;
      this.valueChange.emit(this.value);
    }
  }

  /**
   * 組件初始化
   * 初始化過濾選項列表
   */
  override ngOnInit() {
    super.ngOnInit();
    this.filteredOptions = this.myControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value || ''))
    );
  }

  /**
   * 添加查詢條件
   * 根據當前選中的值生成 SQL 查詢條件
   * @param where 現有的 WHERE 子句
   * @param fieldName 欄位名稱
   * @param condition 條件操作符，默認為 '='
   * @returns 添加條件後的 WHERE 子句
   */
  addWhere(where: string, fieldName: string, condition: string = '='): string {
    if (this.required() && this.value === '') {
      let msg = this.label() + ' 不可空白.';
      alert(msg);
      throw new Error(msg);
    }
    if (this.value) {
      //where += ' AND ('+fieldName + ' = ' + jkLib.quotedStr(this.value)+') ';
      where = jkLib.text2Where(this.value, where, fieldName, condition);
    }
    return where;
  }
}
